import React, { useState, useEffect, useRef, memo } from 'react';
import {
    Box, HStack, Text, Badge, Icon, FormControl, FormLabel,
    Textarea, Select, useToast
} from '@chakra-ui/react';
import { FiCheckCircle } from 'react-icons/fi';
import { getGradeLabelOverride } from '../utils/gradeLabels';

// Helper to format date YYYY-MM-DD
const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

// [A2] gradeLabels 由父層（CommunicationBookPage）依登入者機構傳入；未傳入（沒有 grade_labels
// 設定，天籟現況）時 getGradeLabelOverride 回傳 null，fallback 回現行 `{grade}年級` 文字。
const StudentFeedbackCard = memo(({ student, initialFeedback, signature, onSave, gradeLabels }) => {
    const [feedback, setFeedback] = useState({
        comment: '',
        mood: '',
        health: '',
        ...initialFeedback
    });

    // Update local state when prop changes (e.g. switching to a different day's book).
    // 不能跟 prev merge——換到沒有回饋資料的新一天時 initialFeedback 會是 undefined，
    // {...prev, ...undefined} 等於沒清掉，舊那天打的字就會留著、一存檔變成複製過去。
    // 每次都從空白預設值重新疊加，才能在沒有資料時正確清空。
    useEffect(() => {
        setFeedback({
            comment: '',
            mood: '',
            health: '',
            ...initialFeedback
        });
    }, [initialFeedback]);

    const handleChange = (field, value) => {
        setFeedback(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleBlur = () => {
        // Only trigger save if data has changed or just always save (API handles no-op efficiently usually, or we can check dirty)
        // For simplicity and to ensure sync, we call onSave.
        // We pass the current local feedback back to parent/AP
        onSave(student.id, feedback);
    };

    return (
        <Box
            p={3}
            borderWidth="1px"
            borderRadius="lg"
            _hover={{ borderColor: 'brand.300', shadow: 'sm' }}
            bg={signature ? "green.50" : "white"}
        >
            <HStack mb={2} justify="space-between">
                <Text fontWeight="bold">{student.full_name}</Text>
                <Badge>{getGradeLabelOverride(student.grade, gradeLabels) || `${student.grade}年級`}</Badge>
            </HStack>

            {/* Signature Display Section */}
            {signature ? (
                <Box mb={3} p={2} bg="white" borderRadius="md" border="1px solid" borderColor="green.200">
                    <HStack mb={1}>
                        <Icon as={FiCheckCircle} color="green.500" />
                        <Text fontSize="xs" fontWeight="bold" color="green.700">
                            家長已簽名 {signature.parent_name ? `(${signature.parent_name})` : ''}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                            {formatDate(signature.signed_at)}
                        </Text>
                    </HStack>
                    {signature.comment && (
                        <Text fontSize="sm" color="gray.700" bg="gray.50" p={1} borderRadius="sm">
                            💬 {signature.comment}
                        </Text>
                    )}
                </Box>
            ) : (
                <Badge colorScheme="gray" mb={3} variant="outline">尚未簽名</Badge>
            )}

            <FormControl mb={2}>
                <FormLabel fontSize="xs" color="gray.500">老師評語</FormLabel>
                <Textarea
                    size="sm"
                    rows={2}
                    placeholder="給家長的話..."
                    value={feedback.comment || ''}
                    onChange={(e) => handleChange('comment', e.target.value)}
                    onBlur={handleBlur}
                    bg="white"
                />
            </FormControl>

            <HStack>
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.500">心情</FormLabel>
                    <Select
                        size="xs"
                        value={feedback.mood || ''}
                        onChange={(e) => {
                            handleChange('mood', e.target.value);
                            // For selects, we might want to save immediately on change as well, 
                            // but waiting for blur or effect is also fine. 
                            // Let's rely on blur or explicit trigger. 
                            // Actually for Select, 'onChange' is the interaction complete. 
                            // We should probably trigger save immediately or soon after.
                            // But consistency with Textarea (onBlur) is fine.
                            // Actually, let's call onSave immediately for Select because there is no 'blur' naturally expected by user for a dropdown.
                        }}
                        // Trigger save on blur for Select is good enough, but sometimes focus doesn't leave clearly.
                        // Let's add onBlur to be safe.
                        onBlur={handleBlur}
                        bg="white"
                    >
                        <option value="">-</option>
                        <option value="HAPPY">開心</option>
                        <option value="NORMAL">平靜</option>
                        <option value="SAD">難過</option>
                    </Select>
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="xs" color="gray.500">健康</FormLabel>
                    <Select
                        size="xs"
                        value={feedback.health || ''}
                        onChange={(e) => handleChange('health', e.target.value)}
                        onBlur={handleBlur}
                        bg="white"
                    >
                        <option value="">-</option>
                        <option value="GOOD">良好</option>
                        <option value="SICK">不適</option>
                    </Select>
                </FormControl>
            </HStack>
        </Box>
    );
});

export default StudentFeedbackCard;
